/* eslint-disable id-blacklist */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Alert, AlertTitle, Box } from '@mui/material'
import {
  GridCellParams,
  GridInitialState,
  GridPaginationInitialState,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { InfoIcon } from '@rfh-core/icons'

import { CustomDataGrid } from 'src/common/components'
import { GRID_MARGIN_TOP, MAX_NUMBER_COLS_SORTED } from 'src/common/constants'
import { Entity } from 'src/common/hooks'
import { snackbarUtils } from 'src/common/utils'

import { getRegularityGridColumns } from '../lib'
import ProductRegularityDetails from './ProductRegularityDetails'

type ProductRegelmatig = typeof Entity.ProductRegelmatig

type ProductRegularityGridProps = {
  cacheKey: string
  isLoading: boolean
  paginationStateChange: (value: GridPaginationInitialState) => void
  rowCount: number
  rows: ProductRegelmatig[]
  sortModelChange: (value: GridSortModel) => void
}

export default function ProductRegularityGrid({
  cacheKey,
  isLoading,
  paginationStateChange,
  rowCount,
  rows,
  sortModelChange,
  ...rest
}: Readonly<ProductRegularityGridProps>): JSX.Element {
  const { i18n, t } = useTranslation()
  const history = useHistory()
  const { aanvoerderProductRegelmatigId }: any = useParams()
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [hideAlertContainer, setHideAlertContainer] = useState<boolean>(false)
  // Because we use server side sorting we only allow sorting when there are no more than 300.000 rows
  const maxSortedRows = 300000
  const sortingAllowed = rowCount <= maxSortedRows

  const initialState: GridInitialState = {
    columns: {
      columnVisibilityModel: {
        aanvoerderNummer: true,
        AanvoerderNaam: true,
        ProductCode: true,
        ProductNaam: true,
        vestigingCode: true,
        regelmatig: true,
        UitzonderingDatumVanaf: true,
        UitzonderingDatumTtm: true,
        UitzonderingRedenOmschrijving: true,
        UitzonderingRedenTekst: true,
      },
    },
  }

  const changeLocalStorageSortingModel = (value: GridSortModel) => {
    if (value?.length <= MAX_NUMBER_COLS_SORTED) {
      sortModelChange(value)
    } else {
      // To prevent abuse the maximum number of sorted columns is set to five in the backend
      // we catch this so we can show a friendlier message
      snackbarUtils.warning(t('overviews.maximumSorting'))
    }
  }

  const changePaginationState = (value: GridPaginationInitialState) => {
    paginationStateChange(value)
  }

  const goToRegularityDetails = useCallback(
    (params: GridCellParams) => {
      const id = params?.row?.aanvoerderProductRegelmatigId ?? 0
      if (id > 0) {
        history.push(`/dashboard/supplier-product-regularity/${id}`)
      }
      setDialogOpen(true)
    },
    [history]
  )

  const closeRegularityDetails = useCallback(() => {
    history.push('/dashboard/supplier-product-regularity')
    setDialogOpen(false)
  }, [history])

  useEffect(() => {
    if ((aanvoerderProductRegelmatigId ?? 0) > 0) {
      goToRegularityDetails({
        row: { aanvoerderProductRegelmatigId },
      } as any)
    }
  }, [aanvoerderProductRegelmatigId, goToRegularityDetails])

  const columns = useMemo(
    () => getRegularityGridColumns(sortingAllowed),
    [sortingAllowed]
  )

  return (
    <>
      {!sortingAllowed && !hideAlertContainer && (
        <Box
          sx={{
            marginBottom: GRID_MARGIN_TOP * 2,
            width: '100%',
          }}
        >
          <Alert
            severity='warning'
            icon={<InfoIcon />}
            onClose={() => setHideAlertContainer(true)}
          >
            <AlertTitle>{t('overviews.sortingDisabledAlertTitle')}</AlertTitle>
            {t('overviews.sortingDisabledAlertMessage')}
          </Alert>
        </Box>
      )}
      <Box position={'relative'} marginTop={GRID_MARGIN_TOP} {...rest}>
        <CustomDataGrid
          cacheKey={cacheKey}
          changeGridPaginationInitialState={changePaginationState}
          changeSortModel={changeLocalStorageSortingModel}
          columns={columns}
          rows={rows}
          initialState={initialState}
          language={i18n.language}
          getRowId={(row: ProductRegelmatig) =>
            row.aanvoerderProductRegelmatigId
          }
          gridProps={{
            rowCount,
            onCellClick: goToRegularityDetails,
            loading: isLoading,
          }}
        />
      </Box>
      <ProductRegularityDetails
        open={dialogOpen}
        aanvoerderProductRegelmatigId={aanvoerderProductRegelmatigId}
        onClose={closeRegularityDetails}
      />
    </>
  )
}
